// The enums of the catalog that travel in the contracts: @nexxt/db builds the Postgres enums from these tuples.

/** `catalog.linesheet_status` (@nexxt/db builds the enum from it). */
export const linesheetStatuses = ["draft", "pending", "approved"] as const;
export type LinesheetStatus = (typeof linesheetStatuses)[number];

/** `catalog.price_status`. */
export const priceStatuses = ["pending", "approved"] as const;
export type PriceStatus = (typeof priceStatuses)[number];

/** `catalog.sync_status`: towards Business Central and Joor (FR-M2-06, FR-M2-07). */
export { type SyncStatus, syncStatuses } from "../til-enums";

/** `catalog.attributes_status`: submission and sync of the SKU attributes (M4). */
export const attributesStatuses = ["draft", "pending", "sync_in_progress", "approved"] as const;
export type AttributesStatus = (typeof attributesStatuses)[number];

/** `catalog.product_status`. */
export const productStatuses = ["draft", "active", "discontinued"] as const;
export type ProductStatus = (typeof productStatuses)[number];

/** `catalog.attribute_task_status`: how far the attributes are filled in (FR-M4-03). */
export const attributeTaskStatuses = ["open", "in_progress", "complete", "overdue"] as const;
export type AttributeTaskStatus = (typeof attributeTaskStatuses)[number];

/**
 * Where an attribute task stands for who follows it (ADR-309): how far its SKUs are while the brand fills them in
 * (`attributeTaskStatuses`), then where its attributes are once submitted (`attributesStatuses` but `draft`). The two
 * tuples share no value, so one word names it.
 */
export const attributeTaskStages = [...attributeTaskStatuses, "pending", "sync_in_progress", "approved"] as const;
export type AttributeTaskStage = (typeof attributeTaskStages)[number];

/** `catalog.sync_target`. */
export const syncTargets = ["business_central", "joor"] as const;
export type SyncTarget = (typeof syncTargets)[number];

/**
 * `catalog.sync_run_kind`: what a run sends. The item master of a linesheet to either system (I-01, I-16), or the
 * attributes of its SKUs to Business Central (I-22), months later and on a status of their own (ADR-061).
 */
export const syncRunKinds = ["item_master", "attributes"] as const;
export type SyncRunKind = (typeof syncRunKinds)[number];

/** `catalog.sync_run_status`. */
export const syncRunStatuses = ["queued", "sent", "awaiting_callback", "succeeded", "failed"] as const;
export type SyncRunStatus = (typeof syncRunStatuses)[number];

/** `catalog.sync_mode`: the first sync is always `create` (50-api/catalog.md §2.3). */
export const syncModes = ["create", "update", "overwrite", "only_missing_styles"] as const;
export type SyncMode = (typeof syncModes)[number];

/** `reference.price_channel`: how a price list sells, and so how its prices are calculated (FR-M3-03). */
export const priceChannels = ["exw", "ddp", "agency", "farfetch"] as const;
export type PriceChannel = (typeof priceChannels)[number];

/** `party.brand_type`: the commercial model of a brand, which picks the price formulas. */
export const brandTypes = ["agency", "distribution", "invested"] as const;
export type BrandType = (typeof brandTypes)[number];

/**
 * `catalog.intercompany_price_method` (B-69, ADR-249): how the price of a transfer to another company of the group
 * follows the brand's rule. `price_list`: the wholesale price of a dedicated price list (e.g. DDP-DC-<brand>);
 * `po_cost_markup`: the cost of the SKU's last purchase order, plus a percentage.
 */
export const intercompanyPriceMethods = ["price_list", "po_cost_markup"] as const;
export type IntercompanyPriceMethod = (typeof intercompanyPriceMethods)[number];

/**
 * `catalog.erp_item_file_kind`: why a file of the items to the ERP was written (spec v0.3 §7.3, ADR-257): the sync of a
 * linesheet (I-01), of the attributes of its SKUs (I-22), or the daily resend of the SKUs changed since.
 */
export const erpItemFileKinds = ["linesheet", "attributes", "resend"] as const;
export type ErpItemFileKind = (typeof erpItemFileKinds)[number];

/**
 * `catalog.erp_item_file_status`: `ready` until who imports it says how it went (§7.5); `rejected` when Business
 * Central refused at least one row, each with its reason.
 */
export const erpItemFileStatuses = ["ready", "processed", "rejected"] as const;
export type ErpItemFileStatus = (typeof erpItemFileStatuses)[number];

/**
 * Where a SKU stands with the ERP of a company (§7.3.4): never in a file, in a file still to import ("sent, to import",
 * §7.5), imported, or refused by Business Central.
 */
export const skuErpStates = ["not_sent", "sent", "processed", "rejected"] as const;
export type SkuErpState = (typeof skuErpStates)[number];

/** `platform.import_batch.type`: what the rows of the file are. */
export const importTypes = ["linesheet_lines", "line_prices", "sku_attributes"] as const;
export type ImportType = (typeof importTypes)[number];

/** The first email of a brand for a campaign, or one of those that follow it. */
export const invitationKinds = ["invitation", "reminder"] as const;
export type InvitationKind = (typeof invitationKinds)[number];

/** One tab per function (FR-M3-01), each with its own permission, `pricing.setup.<tab>`. */
export const priceSetupTabs = ["finance", "operations", "sales"] as const;
export type PriceSetupTab = (typeof priceSetupTabs)[number];
